import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { sunoCommsApi } from "../api-base";
import type {
  CommsTemplateCategory,
  CommsTemplateCategoryFilter,
  CommsTemplateCategoryInput,
  CommsTemplateCategoryResponse
} from "../types/template-categories.type";
import type {
  CommsTextTemplate,
  CommsTextTemplateFilters,
  CommsTextTemplateInput
} from "../types/text-templates.type";

export const DEFAULT_COMMS_TEXT_TEMPLATE_ORDERING = "name";

export const textTemplatesEndpoints = sunoCommsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getCommsTemplateCategory: builder.query<CommsTemplateCategory, string>({
      query: (id) => `/template-categories/${id}`,
      providesTags: ["CommsTemplateCategories"]
    }),
    getCommsTemplateCategories: builder.query<
      CommsTemplateCategoryResponse,
      CommsTemplateCategoryFilter | void
    >({
      query: (filters = {}) =>
        `/template-categories${formatValidQueryParamUrlCommaSeparated(
          {
            ...filters,
            is_active: filters.is_active ?? true
          },
          true
        )}`,
      providesTags: ["CommsTemplateCategories"]
    }),
    createCommsTemplateCategory: builder.mutation<
      CommsTemplateCategory,
      CommsTemplateCategoryInput
    >({
      query: (body) => ({
        url: "/template-categories/",
        method: "POST",
        body
      }),
      invalidatesTags: ["CommsTemplateCategories"]
    }),
    updateCommsTemplateCategory: builder.mutation<
      CommsTemplateCategory,
      CommsTemplateCategoryInput
    >({
      query: ({ id, ...body }) => ({
        url: `/template-categories/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["CommsTemplateCategories"]
    }),
    deleteCommsTemplateCategory: builder.mutation<string, string>({
      query: (id) => ({
        url: `/template-categories/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["CommsTemplateCategories", "CommsTextTemplates"]
    }),
    getCommsTextTemplate: builder.query<CommsTextTemplate, string>({
      query: (id) => `/text-templates/${id}`,
      providesTags: ["CommsTextTemplates"]
    }),
    getCommsTextTemplates: builder.query<
      CommsTextTemplate[],
      CommsTextTemplateFilters | void
    >({
      query: (filters = {}) =>
        `/text-templates${formatValidQueryParamUrlCommaSeparated(
          {
            ...filters,
            limit: filters.limit ?? 300,
            offset: filters.offset ?? 0,
            ordering: filters.ordering ?? DEFAULT_COMMS_TEXT_TEMPLATE_ORDERING
          },
          true
        )}`,
      providesTags: ["CommsTextTemplates"]
    }),
    getCommsTextTemplatesCount: builder.query<number, CommsTextTemplateFilters | void>({
      query: (filters = {}) =>
        `/text-templates/count/${formatValidQueryParamUrlCommaSeparated(
          {
            ...filters
          },
          true
        )}`,
      providesTags: ["CommsTextTemplates"]
    }),
    createCommsTextTemplate: builder.mutation<
      CommsTextTemplate,
      CommsTextTemplateInput
    >({
      query: (body) => ({
        url: "/text-templates/",
        method: "POST",
        body
      }),
      invalidatesTags: ["CommsTextTemplates"]
    }),
    updateCommsTextTemplate: builder.mutation<
      CommsTextTemplate,
      CommsTextTemplateInput
    >({
      query: ({ id, ...body }) => ({
        url: `/text-templates/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["CommsTextTemplates"]
    }),
    deleteCommsTextTemplate: builder.mutation<string, string>({
      query: (id) => ({
        url: `/text-templates/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["CommsTextTemplates"]
    })
  })
});

export const {
  useGetCommsTemplateCategoryQuery,
  useGetCommsTemplateCategoriesQuery,
  useCreateCommsTemplateCategoryMutation,
  useUpdateCommsTemplateCategoryMutation,
  useDeleteCommsTemplateCategoryMutation,
  useGetCommsTextTemplateQuery,
  useLazyGetCommsTextTemplateQuery,
  useGetCommsTextTemplatesQuery,
  useLazyGetCommsTextTemplatesQuery,
  useGetCommsTextTemplatesCountQuery,
  useCreateCommsTextTemplateMutation,
  useUpdateCommsTextTemplateMutation,
  useDeleteCommsTextTemplateMutation
} = textTemplatesEndpoints;
